import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import styles from './UploadDashboard.module.css';
import { PRISMAX_DATA_PIPELINE_URL } from '../../../configs/config';
import { SwapMainAppContext } from '../../../context/SwapMainAppContext';
import AssignedJobsSection from './AssignedJobsSection';
import RejectedEpisodePreview from '../../RoboticData/RejectedEpisodePreview';
import { getEpisodeRejectionRows } from './episodeErrors';

// ── Constants ─────────────────────────────────────────────────────────────────

const DURATIONS = [
    { key: '7d',  label: 'Last 7 days' },
    { key: '30d', label: 'Last 30 days' },
    { key: '90d', label: 'Last 90 days' },
    { key: '1y',  label: 'This year' },
    { key: 'all', label: 'All time' },
];

const FAILED_STATUSES = new Set(['DERIVED_VALIDATION_FAILED', 'FAILED', 'QA_REJECTED']);

// ── Helpers ───────────────────────────────────────────────────────────────────

function scoreColor(score) {
    if (score === null || score === undefined) return '#555';
    if (score >= 80) return '#4caf7d';
    if (score >= 60) return '#d4a84b';
    return '#e05c5c';
}

function formatDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-US', {
        month: 'numeric', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: false,
    });
}

// ── Custom chart tooltip ──────────────────────────────────────────────────────

const ChartTooltip = ({ active, payload, label, unit }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className={styles.chartTooltip}>
            <div className={styles.chartTooltipLabel}>{label}</div>
            <div className={styles.chartTooltipValue}>
                {payload[0].value != null ? `${payload[0].value}${unit || ''}` : '—'}
            </div>
        </div>
    );
};

// ── Episode Drawer ────────────────────────────────────────────────────────────

const EpisodeDrawer = ({ upload, onClose, getHeaders, isAdminMode }) => {
    const [filter, setFilter] = useState('all');
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        setFilter('all');
        setExpandedId(null);
    }, [upload]);

    useEffect(() => {
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [onClose]);

    if (!upload) return null;

    const allEpisodes = upload.episodes || [];
    const summary = upload.episode_summary || {};
    const accepted = summary.derived_ready || 0;
    const failed = summary.failed || 0;
    const total = summary.total || 0;
    const accPct = (accepted + failed) > 0 ? Math.round(accepted / (accepted + failed) * 100) : 0;

    const filtered = allEpisodes.filter((ep) => {
        if (filter === 'accepted') return ep.is_derived_ready;
        if (filter === 'rejected') return ep.is_failed;
        return true;
    });

    return (
        <div className={styles.drawerOverlay} onClick={onClose}>
            <div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
                <div className={styles.drawerHeader}>
                    <div className={styles.drawerTopRow}>
                        <div className={styles.drawerTitle}>
                            {upload.upload_id} · {upload.task_name || '—'}
                        </div>
                        <button className={styles.drawerClose} onClick={onClose}>×</button>
                    </div>
                    <div className={styles.drawerMeta}>
                        <div className={styles.drawerMetaItem}>
                            <span className={styles.drawerMetaLabel}>Upload ID</span>
                            <span className={styles.drawerMetaValue}>{upload.upload_id}</span>
                        </div>
                        <div className={styles.drawerMetaItem}>
                            <span className={styles.drawerMetaLabel}>Upload time</span>
                            <span className={styles.drawerMetaValue}>{formatDate(upload.created_at)}</span>
                        </div>
                        <div className={styles.drawerMetaItem}>
                            <span className={styles.drawerMetaLabel}>Episodes</span>
                            <span className={styles.drawerMetaValue}>{total}</span>
                        </div>
                        <div className={styles.drawerMetaItem}>
                            <span className={styles.drawerMetaLabel}>QA score</span>
                            <span
                                className={styles.drawerMetaValue}
                                style={{ color: upload.qa_score != null ? scoreColor(upload.qa_score) : '#555' }}
                            >
                                {upload.qa_score != null ? upload.qa_score : '—'}
                            </span>
                        </div>
                    </div>
                </div>

                <div className={styles.drawerSummaryRow}>
                    <div className={styles.drawerPillAccepted}>&#x2713; {accepted} accepted</div>
                    {failed > 0 && (
                        <div className={styles.drawerPillRejected}>&#x2715; {failed} rejected</div>
                    )}
                    <div className={styles.drawerPfTrack}>
                        <div className={styles.drawerPfFill} style={{ width: `${accPct}%` }} />
                    </div>
                </div>

                <div className={styles.drawerFilterBar}>
                    <span className={styles.drawerFilterLabel}>Show</span>
                    {['all', 'accepted', 'rejected'].map((f) => (
                        <button
                            key={f}
                            className={`${styles.drawerFilterBtn} ${filter === f ? styles.drawerFilterBtnActive : ''}`}
                            onClick={() => { setFilter(f); setExpandedId(null); }}
                        >
                            {f.charAt(0).toUpperCase() + f.slice(1)}
                        </button>
                    ))}
                </div>

                <div className={styles.episodeList}>
                    {filtered.length === 0 && (
                        <div className={styles.episodeEmpty}>No episodes to show.</div>
                    )}
                    {filtered.map((ep) => {
                        const isExpanded = expandedId === ep.episode_id;
                        const dotCls = ep.is_derived_ready
                            ? styles.epDotAccepted
                            : ep.is_failed
                                ? styles.epDotRejected
                                : styles.epDotPending;
                        const badgeCls = ep.is_derived_ready
                            ? styles.epBadgeAccepted
                            : ep.is_failed
                                ? styles.epBadgeRejected
                                : styles.epBadgePending;
                        const badgeLabel = ep.status === 'QA_REJECTED' ? 'QA rejected' : ep.is_derived_ready ? 'Accepted' : ep.is_failed ? 'Rejected' : 'Pending';

                        return (
                            <div key={ep.episode_id}>
                                <div
                                    className={styles.episodeRow}
                                    onClick={() => setExpandedId(isExpanded ? null : ep.episode_id)}
                                >
                                    <span className={styles.epNumber}>
                                        #{String(ep.episode_id).padStart(2, '0')}
                                    </span>
                                    <div className={`${styles.epDot} ${dotCls}`} />
                                    <div className={styles.epInfo}>
                                        <div className={styles.epInfoTitle}>
                                            Episode {ep.episode_key || ep.episode_id}
                                            {ep.is_failed && ep.mcap_file_name && (
                                                <span className={styles.epMcapInline}> · {ep.mcap_file_name}</span>
                                            )}
                                        </div>
                                        <div className={styles.epInfoSub}>
                                            {ep.video_duration_seconds != null ? `${ep.video_duration_seconds}s` : '—'} · 3 cams
                                        </div>
                                    </div>
                                    <span className={`${styles.epBadge} ${badgeCls}`}>{badgeLabel}</span>
                                </div>
                                {isExpanded && ep.is_failed && (
                                    <div className={styles.epDetail}>
                                        <div className={styles.epDetailLabel}>
                                            Rejection reason{(Array.isArray(ep.processing_error?.details?.failed_checks) && ep.processing_error?.details?.failed_checks.filter(Boolean).length > 1)  ? 's' : ''}
                                        </div>
                                        <div className={styles.epErrList}>
                                            {getEpisodeRejectionRows(ep, styles)}
                                        {ep.status === 'QA_REJECTED' && <RejectedEpisodePreview episode={ep} upload={upload} episodeId={ep.episode_id} token={getHeaders().Authorization.replace('Bearer ', '')} isAdmin={isAdminMode}/>}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// ── Machine Dropdown ──────────────────────────────────────────────────────────────

const MachineDropdown = ({ value, options, onChange }) => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const selected = options.find((m) => m.machine_id === value);
    const label = selected ? (selected.product_name || selected.machine_id) : 'All machines';

    useEffect(() => {
        if (!open) return;
        const handleClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    const handleSelect = (machineId) => {
        onChange({ target: { value: machineId } });
        setOpen(false);
    };

    return (
        <div className={styles.dropdownMachineWrap} ref={wrapRef}>
            <button
                type="button"
                className={`${styles.dropdownMachineTrigger}${open ? ` ${styles.dropdownMachineTriggerOpen}` : ''}`}
                onClick={() => setOpen((o) => !o)}
            >
                <span className={styles.dropdownMachineTriggerText}>{label}</span>
                <svg className={styles.dropdownMachineChevron} width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            {open && (
                <div className={styles.dropdownMachineMenu}>
                    <button
                        type="button"
                        className={`${styles.dropdownMachineOption}${!value ? ` ${styles.dropdownMachineOptionActive}` : ''}`}
                        onClick={() => handleSelect('')}
                    >
                        All machines
                    </button>
                    {options.map((m) => (
                        <button
                            key={m.machine_id}
                            type="button"
                            className={`${styles.dropdownMachineOption}${m.machine_id === value ? ` ${styles.dropdownMachineOptionActive}` : ''}`}
                            onClick={() => handleSelect(m.machine_id)}
                        >
                            {m.product_name || m.machine_id}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// ── Pass/Fail bar cell ────────────────────────────────────────────────────────

const PFBar = ({ summary }) => {
    const accepted = summary?.derived_ready || 0;
    const failed = summary?.failed || 0;
    const pending = summary?.pending || 0;
    const total = summary?.total || 0;

    if (total === 0) return <span className={styles.scorePending}>—</span>;

    if (pending > 0 && accepted === 0 && failed === 0) {
        return (
            <div className={styles.pfWrap}>
                <div className={styles.pfCounts}>
                    <span className={styles.pfPending}>Pending review</span>
                </div>
                <div className={styles.pfBar}>
                    <div className={styles.pfSegmentPending} style={{ width: '100%' }} />
                </div>
            </div>
        );
    }

    const scored = accepted + failed;
    const accPct = scored > 0 ? Math.round(accepted / scored * 100) : 0;
    const rejPct = scored > 0 ? Math.round(failed / scored * 100) : 0;
    return (
        <div className={styles.pfWrap}>
            <div className={styles.pfCounts}>
                <span className={styles.pfAccepted}>{accepted} accepted</span>
                {failed > 0 && <span className={styles.pfRejected}>{failed} rejected</span>}
            </div>
            <div className={styles.pfBar}>
                <div className={styles.pfSegmentAccepted} style={{ width: `${accPct}%` }} />
                <div className={styles.pfSegmentRejected} style={{ width: `${rejPct}%` }} />
            </div>
        </div>
    );
};

// ── Quality score cell ────────────────────────────────────────────────────────

const QualityScore = ({ score }) => {
    if (score === null || score === undefined) {
        return <span className={styles.scorePending}>Scoring in progress</span>;
    }
    const color = scoreColor(score);
    return (
        <div className={styles.scoreWrap}>
            <div className={styles.scoreDot} style={{ background: color }} />
            <span style={{ color, fontSize: '12px' }}>{score}</span>
        </div>
    );
};

// ── Metric card ───────────────────────────────────────────────────────────────

const MetricCard = ({ label, loading, error, children }) => (
    <div className={styles.metricCard}>
        <div className={styles.metricLabel}>{label}</div>
        {loading ? (
            <div className={styles.metricSkeleton} />
        ) : error ? (
            <div className={styles.metricError}>—</div>
        ) : (
            children
        )}
    </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const UploadDashboard = ({ adminUserId = null }) => {
    const { gatewayToken } = useContext(SwapMainAppContext);
    const navigate = useNavigate();
    const isAdminMode = adminUserId !== null;

    const [duration, setDuration] = useState('30d');
    const [selectedMachine, setSelectedMachine] = useState('');
    const [page, setPage] = useState(1);
    const pageSize = 5;

    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryError, setSummaryError] = useState('');

    const [uploads, setUploads] = useState([]);
    const [uploadsTotal, setUploadsTotal] = useState(0);
    const [uploadsTotalPages, setUploadsTotalPages] = useState(1);
    const [machineOptions, setMachineOptions] = useState([]);
    const [uploadsLoading, setUploadsLoading] = useState(false);
    const [uploadsError, setUploadsError] = useState('');

    const [selectedUpload, setSelectedUpload] = useState(null);

    // ── Auth helpers ──────────────────────────────────────────────────────────

    const getHeaders = useCallback(() => {
        if (isAdminMode) {
            const adminToken = localStorage.getItem('prismax_admin_token') || '';
            return { Authorization: `Bearer ${adminToken}` };
        }
        const token = gatewayToken || localStorage.getItem('gatewayToken') || '';
        return { Authorization: `Bearer ${token}` };
    }, [isAdminMode, gatewayToken]);

    const buildParams = useCallback((extra) => {
        const params = { duration, ...extra };
        if (isAdminMode) params.user_id = adminUserId;
        return new URLSearchParams(params).toString();
    }, [duration, isAdminMode, adminUserId]);

    // ── Fetch summary ─────────────────────────────────────────────────────────

    const fetchSummary = useCallback(async (dur) => {
        setSummaryLoading(true);
        setSummaryError('');
        try {
            const base = isAdminMode
                ? `${PRISMAX_DATA_PIPELINE_URL}/data/admin/operator-dashboard/summary`
                : `${PRISMAX_DATA_PIPELINE_URL}/data/operator-dashboard/summary`;
            const qs = new URLSearchParams(isAdminMode
                ? { duration: dur, user_id: adminUserId }
                : { duration: dur }).toString();
            const res = await fetch(`${base}?${qs}`, { headers: getHeaders() });
            if (!res.ok) throw new Error('Failed to load summary');
            const json = await res.json();
            setSummary(json.data || null);
        } catch (err) {
            console.error(err);
            setSummaryError('Failed to load dashboard summary.');
        } finally {
            setSummaryLoading(false);
        }
    }, [isAdminMode, adminUserId, getHeaders]);

    // ── Fetch uploads ─────────────────────────────────────────────────────────

    const fetchUploads = useCallback(async (dur, machineId, pg) => {
        setUploadsLoading(true);
        setUploadsError('');
        try {
            const base = isAdminMode
                ? `${PRISMAX_DATA_PIPELINE_URL}/data/admin/operator-dashboard/uploads`
                : `${PRISMAX_DATA_PIPELINE_URL}/data/operator-dashboard/uploads`;
            const raw = { duration: dur, page: pg, page_size: pageSize };
            if (machineId) raw.machine_id = machineId;
            if (isAdminMode) raw.user_id = adminUserId;
            const qs = new URLSearchParams(raw).toString();
            const res = await fetch(`${base}?${qs}`, { headers: getHeaders() });
            if (!res.ok) throw new Error('Failed to load uploads');
            const json = await res.json();
            setUploads(json.data || []);
            setUploadsTotal(json.pagination?.total || 0);
            setUploadsTotalPages(json.pagination?.total_pages || 1);
            if (json.machines?.length) setMachineOptions(json.machines);
        } catch (err) {
            console.error(err);
            setUploadsError('Failed to load upload history.');
        } finally {
            setUploadsLoading(false);
        }
    }, [isAdminMode, adminUserId, getHeaders]);

    // ── Effects ───────────────────────────────────────────────────────────────

    useEffect(() => {
        fetchSummary(duration);
    }, [duration, fetchSummary]);

    useEffect(() => {
        fetchUploads(duration, selectedMachine, page);
    }, [duration, selectedMachine, page, fetchUploads]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleDurationChange = (key) => {
        setDuration(key);
        setPage(1);
        setSelectedUpload(null);
    };

    const handleMachineChange = (e) => {
        setSelectedMachine(e.target.value);
        setPage(1);
        setSelectedUpload(null);
    };

    const handleRowClick = (upload) => {
        setSelectedUpload((prev) => prev?.upload_id === upload.upload_id ? null : upload);
    };

    const handleContinueUpload = (e, row) => {
        e.stopPropagation();
        navigate('/data/upload', {
            state: {
                resumeUpload: {
                    upload_id: row.upload_id,
                    task_id: row.task_id,
                    machine_id: row.machine_id,
                    status: row.status,
                },
            },
        });
    };

    // ── Pagination ────────────────────────────────────────────────────────────

    const pageButtons = useMemo(() => {
        const maxVisible = 5;
        let start = Math.max(1, page - Math.floor(maxVisible / 2));
        let end = Math.min(uploadsTotalPages, start + maxVisible - 1);
        if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
        const pages = [];
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    }, [page, uploadsTotalPages]);

    const durationLabel = DURATIONS.find((d) => d.key === duration)?.label || '';

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className={styles.pageContainer}>
            <div className={styles.page}>

                {/* Duration pills */}
                <div className={styles.rangeBar}>
                    <span className={styles.rangeLabel}>View data for</span>
                    {DURATIONS.map((d) => (
                        <button
                            key={d.key}
                            className={`${styles.rangeBtn} ${duration === d.key ? styles.rangeBtnActive : ''}`}
                            onClick={() => handleDurationChange(d.key)}
                        >
                            {d.label}
                        </button>
                    ))}
                </div>

                {/* Metric cards */}
                <div className={styles.metricsGrid}>
                    <MetricCard label="Avg QA Score" loading={summaryLoading} error={!!summaryError}>
                        {summary && (
                            <>
                                <div className={styles.qaValueRow}>
                                    <span
                                        className={styles.metricValue}
                                        style={{ color: scoreColor(summary.avg_qa_score) }}
                                    >
                                        {summary.avg_qa_score ?? '—'}
                                    </span>
                                    <span className={styles.metricUnit}>/100</span>
                                </div>
                                <div className={styles.qaBarTrack}>
                                    <div
                                        className={styles.qaBarFill}
                                        style={{
                                            width: `${summary.avg_qa_score ?? 0}%`,
                                            background: scoreColor(summary.avg_qa_score),
                                        }}
                                    />
                                </div>
                                {summary.avg_qa_score_delta != null && (
                                    <div className={`${styles.metricSub} ${summary.avg_qa_score_delta >= 0 ? styles.metricUp : styles.metricDown}`}>
                                        {summary.avg_qa_score_delta >= 0 ? '↑' : '↓'} {Math.abs(summary.avg_qa_score_delta)} pts vs prior period
                                    </div>
                                )}
                            </>
                        )}
                    </MetricCard>

                    <MetricCard label="Total Hrs" loading={summaryLoading} error={!!summaryError}>
                        {summary && (
                            <>
                                <div className={styles.metricValue}>
                                    {summary.total_hrs != null ? `${summary.total_hrs.toFixed(2)} h` : '—'}
                                </div>
                                {summary.total_hrs_delta_pct != null && (
                                    <div className={`${styles.metricSub} ${summary.total_hrs_delta_pct >= 0 ? styles.metricUp : styles.metricDown}`}>
                                        {summary.total_hrs_delta_pct >= 0 ? '↑' : '↓'} {Math.abs(summary.total_hrs_delta_pct)}% vs prior period
                                    </div>
                                )}
                            </>
                        )}
                    </MetricCard>

                    <MetricCard label="Avg Hrs / Day" loading={summaryLoading} error={!!summaryError}>
                        {summary && (
                            <>
                                <div className={styles.metricValue}>
                                    {summary.avg_hrs_per_day != null ? `${summary.avg_hrs_per_day} h` : '—'}
                                </div>
                                <div className={styles.metricSub}>
                                    {duration === 'all' ? 'All-time avg' : `Rolling ${durationLabel.toLowerCase()} avg`}
                                </div>
                            </>
                        )}
                    </MetricCard>
                </div>

                {/* Charts */}
                <div className={styles.chartsRow}>
                    <div className={styles.chartCard}>
                        <div className={styles.chartTitle}>QA score trend — {durationLabel}</div>
                        {summaryLoading ? (
                            <div className={styles.chartPlaceholder} />
                        ) : summary?.qa_score_trend?.length > 0 ? (
                            <ResponsiveContainer width="100%" height={110}>
                                <LineChart data={summary.qa_score_trend} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                                    <XAxis dataKey="label" tick={{ fill: '#444', fontSize: 9 }} axisLine={{ stroke: 'rgba(255,255,255,0.06)' }} tickLine={false} />
                                    <YAxis domain={[70, 100]} tick={{ fill: '#444', fontSize: 9 }} axisLine={{ stroke: 'rgba(255,255,255,0.06)' }} tickLine={false} />
                                    <Tooltip content={<ChartTooltip />} />
                                    <Line type="monotone" dataKey="score" stroke="#4caf7d" strokeWidth={1.5} dot={{ r: 2, fill: '#4caf7d' }} activeDot={{ r: 3 }} connectNulls={false} isAnimationActive={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className={styles.chartEmpty}>No QA data for this period</div>
                        )}
                    </div>

                    <div className={styles.chartCard}>
                        <div className={styles.chartTitle}>Episode hrs / day — {durationLabel}</div>
                        {summaryLoading ? (
                            <div className={styles.chartPlaceholder} />
                        ) : summary?.episode_hrs_trend?.length > 0 ? (
                            <ResponsiveContainer width="100%" height={110}>
                                <LineChart data={summary.episode_hrs_trend} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                                    <XAxis dataKey="label" tick={{ fill: '#444', fontSize: 9 }} axisLine={{ stroke: 'rgba(255,255,255,0.06)' }} tickLine={false} />
                                    <YAxis tick={{ fill: '#444', fontSize: 9 }} tickFormatter={(v) => `${v}h`} axisLine={{ stroke: 'rgba(255,255,255,0.06)' }} tickLine={false} />
                                    <Tooltip content={<ChartTooltip unit="h" />} />
                                    <Line type="monotone" dataKey="hrs" stroke="#5a9fd4" strokeWidth={1.5} dot={{ r: 2, fill: '#5a9fd4' }} activeDot={{ r: 3 }} connectNulls={false} isAnimationActive={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className={styles.chartEmpty}>No data for this period</div>
                        )}
                    </div>
                </div>

                {/* Upload history table */}
                <div className={styles.tableSection}>
                    <div className={styles.tableHeaderRow}>
                        <div className={styles.tableLabel}>
                            Upload history
                            <span className={styles.tableLabelSub}>{durationLabel}</span>
                        </div>
                        <div className={styles.filterRow}>
                            <MachineDropdown
                                value={selectedMachine}
                                options={machineOptions}
                                onChange={handleMachineChange}
                            />
                        </div>
                    </div>

                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th className={styles.colUploadId}>Upload ID</th>
                                    <th className={styles.colTaskName}>Task name</th>
                                    <th className={styles.colUploadTime}>Upload time</th>
                                    <th className={styles.colEpisodes}>Episodes</th>
                                    <th className={styles.colTotalHrs}>Total hrs</th>
                                    <th className={styles.colQualityScore}>Quality score</th>
                                    <th className={styles.colPassFail}>Pass / Fail</th>
                                    <th className={styles.colAction} />
                                </tr>
                            </thead>
                            <tbody>
                                {uploads.map((row) => {
                                    const isSelected = selectedUpload?.upload_id === row.upload_id;
                                    const isUploading = String(row.status || '').toUpperCase() === 'UPLOADING';
                                    return (
                                        <tr
                                            key={row.upload_id}
                                            className={`${styles.tableRow} ${isSelected ? styles.tableRowSelected : ''}`}
                                            onClick={() => handleRowClick(row)}
                                        >
                                            <td>
                                                <span className={styles.uploadId}>{row.upload_id}</span>
                                            </td>
                                            <td>
                                                <span className={styles.taskName}>{row.task_name || '—'}</span>
                                            </td>
                                            <td className={styles.dateCell}>{formatDate(row.created_at)}</td>
                                            <td className={styles.epNum}>{row.episode_summary?.total ?? '—'}</td>
                                            <td className={styles.epNum}>
                                                {row.total_hrs > 0 
                                                ? `${row.total_hrs.toFixed(2)} h`
                                                : '—'}
                                            </td>
                                            <td><QualityScore score={row.qa_score} /></td>
                                            <td><PFBar summary={row.episode_summary} /></td>
                                            <td className={styles.actionCell} onClick={(e) => e.stopPropagation()}>
                                                {isUploading && !isAdminMode && (
                                                    <button
                                                        className={styles.continueBtn}
                                                        onClick={(e) => handleContinueUpload(e, row)}
                                                    >
                                                        Continue
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {!uploadsLoading && !uploadsError && uploads.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className={styles.emptyCell}>
                                            No uploads for this period.
                                        </td>
                                    </tr>
                                )}
                                {uploadsError && (
                                    <tr>
                                        <td colSpan={8} className={styles.errorCell}>{uploadsError}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        <div className={styles.paginator}>
                            <div className={styles.pageInfo}>
                                {uploadsLoading
                                    ? 'Loading...'
                                    : `Showing ${uploads.length} of ${uploadsTotal}`}
                            </div>
                            <div className={styles.pageButtons}>
                                {pageButtons.map((p) => (
                                    <button
                                        key={p}
                                        className={`${styles.pageBtn} ${p === page ? styles.pageBtnActive : ''}`}
                                        onClick={() => setPage(p)}
                                        disabled={uploadsLoading}
                                    >
                                        {p}
                                    </button>
                                ))}
                                {page < uploadsTotalPages && (
                                    <button
                                        className={styles.pageBtn}
                                        onClick={() => setPage((p) => p + 1)}
                                        disabled={uploadsLoading}
                                    >
                                        →
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <AssignedJobsSection isAdminMode={isAdminMode} adminUserId={adminUserId} />

                {/* Episode drawer */}
                {selectedUpload && (
                    <EpisodeDrawer
                        upload={selectedUpload}
                        getHeaders={getHeaders}
                        isAdminMode={isAdminMode}
                        onClose={() => setSelectedUpload(null)}
                    />
                )}
            </div>
        </div>
    );
};

export default UploadDashboard;
